"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography, Stack, Link, Checkbox, CircularProgress } from "@mui/material";
import { format, addWeeks, startOfWeek, addDays, isBefore, isSameDay } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowCircle } from "./arrowUp";
import { StyledButton } from "../../../../styled-mui";
import ConsentBox from "./consentBox";
import { getReCaptchaToken } from "../../../../utils/storeLocatorUtils";
import AsyncCall from "../../../../_foundation/gtm/async.service";
import { CLICK } from "../../../../_foundation/constants/gtm";
import demandeService from "../../../../_foundation/apis/transaction/demande.service";
import { swrOptionsSubmitNoRetry } from "../../../../utils/hooks/swr/constants";
import { HANDLE_SUCCESS_MESSAGE_ACTION } from "../../../../redux/actions/success";
import { useDispatch } from "react-redux";
import useSWR from "swr";
import DateSlotSkeleton from "./skeleton/dateSlotSkeleton";

export type SlotType = {
  startTime: string;
  endTime: string;
  locationId: string;
  locationName: string;
  disabled?: boolean;
};

export type ConfirmationType = "APPOINTMENT" | "CALLBACK";
export interface DateSlotSubmitPayload {
  date: string;
  locationId: string;
  slot: SlotType | null;
  formData?: any;
  confirmationType: ConfirmationType;
}

interface DateSlotProps {
  onClose?: () => void;
  onSubmit?: (payload: DateSlotSubmitPayload) => void;
  formData?: any;
}

const DateSlot: React.FC<DateSlotProps> = ({ onSubmit, onClose, formData }) => {
  const today = new Date();
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<SlotType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const [isWeekChecked, setIsWeekChecked] = useState(false);

  const weekDates = useMemo(() => {
    const start = startOfWeek(addWeeks(today, weekOffset), { weekStartsOn: 1 });
    return Array.from({ length: 6 }, (_, i) => addDays(start, i));
  }, [weekOffset]);

  const formatKey = (d: Date) => format(d, "yyyy-MM-dd");

  const { data: timeSlots } = useSWR(
    formData ? { storeCode: formData.codeMag, universe: formData.Univers, type: formData.type } : null,
    demandeService.getTimeSlots,
    swrOptionsSubmitNoRetry
  );

  const availability = useMemo(() => {
    return timeSlots?.data?.response?.availability ?? [];
  }, [timeSlots]);

  const firstActiveDate = useMemo(() => {
    if (!availability.length) return null;

    for (const d of weekDates) {
      const key = formatKey(d);
      const dayData = availability.find((a) => a.date === key);
      const hasSlots = (dayData?.slots ?? []).length > 0;
      const isPast = isBefore(d, today) && !isSameDay(d, today);

      if (!isPast && hasSlots) return key;
    }
    return null;
  }, [weekDates, availability]);

  useEffect(() => {
    setSelectedDate(firstActiveDate);
    setSelectedSlot(null);
  }, [firstActiveDate]);

  console.log("timeSlots", timeSlots);

  const nextWeek = () => setWeekOffset((w) => Math.min(3, w + 1));
  const prevWeek = () => setWeekOffset((w) => Math.max(0, w - 1));

  // const weekHasSlots = firstActiveDate !== null;

  const weekHasSlots = availability.some((day) => {
    const dayDate = new Date(day.date);
    return !isBefore(dayDate, today) && day.slots && day.slots.length > 0;
  });

  const handleSubmit = async () => {
    if (!selectedDate || !selectedSlot || !formData) return;

    setIsSubmitting(true);

    try {
      const token = await getReCaptchaToken("AppointmentForm");

      // Format slot for comment
      const slotComment = `${format(new Date(selectedDate), "dd/MM/yyyy")} : ${selectedSlot.startTime} - ${
        selectedSlot.endTime
      }`;

      // Use existing comment if available
      const comment = formData.comment
        ? `Créneau souhaité : ${slotComment}\n${formData.comment}`
        : `Créneau souhaité : ${slotComment}`;

      const { UniversSingle, ...payloadData } = formData;

      const payload: any = {
        ...payloadData,
        // date: selectedDate,
        // codeMag: selectedSlot.locationId,
        // slot: selectedSlot,
        // recaptchaToken: token,
        Univers: formData.UniversSingle,
        comment,
      };

      const response = await demandeService.addDemandeRDV({ body: payload });

      if (response?.data) {
        onSubmit?.({
          date: selectedDate,
          locationId: selectedSlot.locationId,
          slot: selectedSlot,
          formData: payload,
          confirmationType: "APPOINTMENT",
        });

        setSelectedDate(null);
        setSelectedSlot(null);
      }
    } catch (err) {
      console.error("Appointment submission failed:", err);
      dispatch(
        HANDLE_SUCCESS_MESSAGE_ACTION({
          key: "error-message.APPOINTMENT_FAILED",
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallbackRequest = async () => {
    if (!formData) return;

    setIsSubmitting(true);

    try {
      const token = await getReCaptchaToken("AppointmentForm");
      const { UniversSingle, ...payloadData } = formData;
      const payload: any = {
        ...payloadData,
        Univers: formData.UniversSingle,
      };

      const response = await demandeService.addDemandeRDV({ body: payload });

      if (response?.data) {
        // dispatch(
        //   HANDLE_SUCCESS_MESSAGE_ACTION({
        //     key: "success-message.CALLBACK_REQUESTED",
        //   })
        // );

        onSubmit?.({
          date: "",
          locationId: "",
          slot: null as any,
          formData: payload,
          confirmationType: "CALLBACK",
        });
      }
    } catch (err) {
      console.error("Callback request failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!availability || availability.length === 0) {
      setIsWeekChecked(true);
      return;
    }

    let offset = 0;
    const maxOffset = 3;

    while (offset <= maxOffset) {
      const start = startOfWeek(addWeeks(today, offset), { weekStartsOn: 1 });
      const weekDatesCheck = Array.from({ length: 6 }, (_, i) => addDays(start, i));

      const weekHasSlotsCheck = weekDatesCheck.some((date) => {
        const key = format(date, "yyyy-MM-dd");
        const dayData = availability.find((a) => a.date === key);
        const hasSlots = (dayData?.slots ?? []).length > 0;
        const isPast = isBefore(date, today) && !isSameDay(date, today);
        return !isPast && hasSlots;
      });

      if (weekHasSlotsCheck) {
        setWeekOffset(offset);
        break;
      }

      offset += 1;
    }

    setIsWeekChecked(true);
  }, [availability]);

  const hasAvailabilityInAnyWeek = useMemo(() => {
    if (!availability || availability.length === 0) return false;

    const maxOffset = 3;

    for (let offset = 0; offset <= maxOffset; offset++) {
      const start = startOfWeek(addWeeks(today, offset), { weekStartsOn: 1 });
      const weekDatesCheck = Array.from({ length: 6 }, (_, i) => addDays(start, i));

      const weekHasSlots = weekDatesCheck.some((date) => {
        const key = format(date, "yyyy-MM-dd");
        const dayData = availability.find((a) => a.date === key);
        const isPast = isBefore(date, today) && !isSameDay(date, today);
        return !isPast && (dayData?.slots?.length ?? 0) > 0;
      });

      if (weekHasSlots) return true;
    }

    return false;
  }, [availability, today]);

  if (!timeSlots || !isWeekChecked) {
    return <DateSlotSkeleton />;
  }

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          color: "#848484",
          fontSize: "16px",
          mt: { xs: 2, md: 0 },
          mb: 3,
          fontWeight: 700,
          lineHeight: "20px",
        }}>
        Choisissez votre créneau
      </Typography>

      <Box sx={{ p: 4, bgcolor: "#F6F9FC", borderRadius: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 6, alignItems: "center" }}>
          <Typography
            variant="h6"
            sx={{
              textTransform: "capitalize",
              color: "#212121",
              fontSize: "16px",
              fontWeight: 600,
              lineHeight: "32px",
            }}>
            {format(weekDates[0], "MMMM yyyy", { locale: fr })}
          </Typography>

          <Stack direction="row" spacing={2}>
            <Button
              variant="text"
              onClick={prevWeek}
              disabled={weekOffset === 0}
              sx={{
                p: 0,
                minWidth: "auto",
                border: 0,
                bgcolor: "transparent",
                "&:hover, &:active, &:focus": { bgcolor: "transparent", border: 0, boxShadow: "none" },
              }}>
              <ArrowCircle size={37} direction="left" />
            </Button>

            <Button
              variant="text"
              onClick={nextWeek}
              disabled={weekOffset === 3}
              sx={{
                p: 0,
                minWidth: "auto",
                border: 0,
                bgcolor: "transparent",
                "&:hover, &:active, &:focus": { bgcolor: "transparent", border: 0, boxShadow: "none" },
              }}>
              <ArrowCircle size={37} />
            </Button>
          </Stack>
        </Box>

        {/* Week Dates */}
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
            alignItems: "center",
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
          mb={6}>
          {weekDates.map((date) => {
            const key = formatKey(date);
            const dayData = availability.find((a) => a.date === key);
            const disabled = (isBefore(date, today) && !isSameDay(date, today)) || !dayData?.slots.length;
            return (
              <Button
                key={key}
                fullWidth
                disabled={disabled}
                onClick={() => {
                  setSelectedDate(key);
                  setSelectedSlot(null);
                }}
                sx={{
                  display: "flex",
                  width: { xs: "calc((100% - 24px) / 3)", sm: "71px" },
                  padding: "18px 14px",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "6px",
                  borderRadius: "8px",
                  color: "#333",
                  border: selectedDate === key ? "2px solid #3D7290" : "2px solid #E7EAEE",
                  background: "#FFF",
                  opacity: disabled ? 0.5 : 1,
                  "&:hover": { borderColor: selectedDate === key ? "#3D7290" : "#E7EAEE", background: "#FFF" },
                  textTransform: "none",
                }}>
                <Typography variant="body2" fontWeight={600}>
                  {format(date, "EEE", { locale: fr }).toUpperCase().replace(".", "")}
                </Typography>
                <Typography>{format(date, "d", { locale: fr })}</Typography>
              </Button>
            );
          })}
        </Box>

        {/* Time Slots */}
        {selectedDate &&
          availability
            .find((a) => a.date === selectedDate)
            ?.slots.map((slot) => {
              const isDisabled = slot.disabled;
              const isActive = selectedSlot === slot;

              return (
                <Box key={slot.startTime + slot.endTime + slot.locationId} sx={{ mb: 3, display: "flex" }}>
                  <Button
                    fullWidth
                    disabled={isDisabled}
                    onClick={() => setSelectedSlot(slot)}
                    sx={{
                      display: "flex",
                      height: "45px",
                      padding: "14px",
                      flexDirection: "column",
                      alignItems: "center",
                      alignSelf: "stretch",
                      borderRadius: "8px",
                      border: isActive ? "2px solid #3D7290" : "2px solid #E7EAEE",
                      backgroundColor: "#FFF",
                      opacity: isDisabled ? 0.4 : 1,
                      color: "#000",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#FFF",
                        borderColor: isActive ? "#3D7290" : "2px solid #E7EAEE",
                      },
                    }}>
                    {slot.startTime}h - {slot.endTime}h
                  </Button>
                </Box>
              );
            })}

        {/* No Slots Message */}
        {!weekHasSlots ? (
          <Box
            sx={{
              backgroundColor: "#fff",
              p: 6,
              borderRadius: 2,
              textAlign: "center",
              maxWidth: 395,
              mx: "auto",
            }}>
            {/* Title */}
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, lineHeight: 1.4 }}>
              Aucun créneau disponible
              {hasAvailabilityInAnyWeek && " pour cette semaine"}
            </Typography>

            {/* Main button */}
            {/* <StyledButton
              variant="contained"
              color="secondary"
              type="submit"
              //   onClick={handleSubmit}
              sx={{
                width: { xs: "100%", sm: "auto" },
              }}>
              VOIR LES PROCHAINES DISPONIBILITÉS
            </StyledButton> */}

            {/* Secondary link */}
            <Typography sx={{ mt: 3, fontSize: "14px", fontWeight: 600 }}>
              <Link
                component="button"
                onClick={handleCallbackRequest}
                underline="always"
                sx={{
                  cursor: "pointer",
                  fontSize: "14px",
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  color: "#333333",
                  textDecorationColor: "#333333",
                }}>
                demandez à être rappelé
              </Link>
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ mt: 3, fontSize: "14px", fontWeight: 400, color: "#333333" }}>
                Aucun créneau ne vous convient ?{" "}
                <Link
                  component="button"
                  onClick={handleCallbackRequest}
                  underline="always"
                  sx={{
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#333333",
                    textDecorationColor: "#333333",
                  }}>
                  Être rappelé
                </Link>
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* <ConsentBox /> */}

      <Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          sx={{ pt: { xs: 3, md: 6 } }}
          justifyContent="right"
          alignItems="center"
          spacing={1.5}>
          <StyledButton
            variant="outline"
            color="black"
            type="button"
            sx={{ width: { xs: "100%", sm: "auto" }, display: { xs: "none", md: "inline-flex" } }}
            onClick={onClose}>
            Retour
          </StyledButton>

          <StyledButton
            variant="contained"
            color="primary"
            type="submit"
            disabled={!selectedSlot || isSubmitting}
            onClick={handleSubmit}
            startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
            sx={{
              width: { xs: "100%", sm: "auto" },
              backgroundColor: "#C41E3B",
              "&:hover": {
                backgroundColor: "#a81832",
              },
            }}>
            Confirmer mon rendez-vous
          </StyledButton>
        </Stack>
      </Box>
    </>
  );
};

export default DateSlot;
